import { useBrand } from "hooks/useBrand";
import { SITE_ORIGIN, useSeo } from "hooks/useSeo";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";

import FeatureIcon from "./FeatureIcon";
import LandingFooter from "./LandingFooter";
import type { FeatureItem, TreeNode } from "./landingPage.types";
import TrendingTrees from "./TrendingTrees";

import styles from "./LandingPage.module.css";

/** Feature cards, in display order. Copy lives under `features.<icon>` in the namespace. */
const features: FeatureItem[] = [
	{ icon: "tree" },
	{ icon: "relations" },
	{ icon: "collaboration" },
	{ icon: "community" }
];

const treeNodes: TreeNode[] = [
	{ className: styles.nodePrimary, id: "primary" },
	{ className: styles.nodeLeft, id: "left" },
	{ className: styles.nodeMiddle, id: "middle" },
	{ className: styles.nodeRight, id: "right" },
	{ className: styles.nodeLowerLeft, id: "lower-left" },
	{ className: styles.nodeLowerMiddle, id: "lower-middle" },
	{ className: styles.nodeLowerRight, id: "lower-right" }
];

const LandingPage = () => {
	const { t, ready } = useTranslation(["landingPage", "common", "seo"]);
	const { brand } = useBrand();

	// The site's root entity: what Tree Tier is, so a search engine can render a knowledge panel and
	// attribute every other page on the domain back to one organisation.
	// Held back until the namespaces land, so a raw key never reaches the tab title or a crawler.
	useSeo({
		title: ready ? t("seo:home.title") : null,
		description: ready ? t("seo:home.description") : null,
		canonicalPath: Paths.root,
		jsonLd: {
			"@context": "https://schema.org",
			"@type": "WebSite",
			name: brand,
			url: SITE_ORIGIN,
			description: t("seo:home.description"),
			publisher: { "@type": "Organization", name: brand, url: SITE_ORIGIN }
		}
	});

	return (
		<main className={styles.page}>
			<header className={styles.header}>
				<Link aria-label={t("common:brandHome", { brand })} className={styles.brand} to={Paths.root}>
					<img alt="" className={styles.brandMark} src="/assets/landing/main-logo.png" />
					<span>{brand}</span>
				</Link>

				<nav aria-label={t("landingPage:nav.label")} className={styles.nav}>
					{/* Routed rather than a bare anchor so ScrollToTop owns the jump: the page is still
					    settling right after load, and a native jump lands off target. */}
					<Link to="#features">{t("landingPage:nav.features")}</Link>
					{/* Signed-out visitors are bounced off /explore and /plans, so route them via sign-in
					    with a redirect - they still land on the page they asked for. */}
					<Link to={Paths.explore}>{t("landingPage:nav.explore")}</Link>
					<Link to={Paths.plans}>{t("landingPage:nav.pricing")}</Link>
					<Link to={Paths.signIn}>{t("landingPage:nav.signIn")}</Link>
				</nav>

				<Link className={`${styles.cta} ${styles.headerCta}`} to={Paths.signUp}>
					{t("landingPage:startBuildingFree")}
				</Link>
			</header>

			<section className={styles.hero}>
				<div className={styles.heroCopy}>
					<h1>
						{t("landingPage:hero.titleLine1")}
						<br />
						{t("landingPage:hero.titleLine2")}
						<br />
						{t("landingPage:hero.titleLine3")}
					</h1>
					<div aria-hidden="true" className={styles.flourish} />
					<p>{t("landingPage:hero.subtitle", { brand })}</p>

					<div className={styles.heroActions}>
						<Link className={styles.cta} to={Paths.signUp}>
							{t("landingPage:startBuildingFree")}
						</Link>
						<Link className={styles.secondaryCta} to={Paths.explore}>
							{t("landingPage:hero.exploreCta")}
						</Link>
					</div>
				</div>

				<section aria-label={t("landingPage:preview.label")} className={styles.preview}>
					<div className={styles.previewRail}>
						<img alt="" src="/assets/landing/main-logo.png" />
						<span />
						<span />
						<span />
						<span />
						<span />
					</div>
					<div className={styles.previewContent}>
						<div className={styles.previewTopbar}>
							<div>
								<strong>{t("landingPage:preview.title")}</strong>
								<span>{t("landingPage:preview.subtitle")}</span>
							</div>
							<div aria-hidden="true" className={styles.previewControls}>
								<span>-</span>
								<span>100%</span>
								<span>+</span>
							</div>
						</div>

						<div className={styles.treeMap}>
							<svg
								aria-hidden="true"
								className={styles.treeLines}
								preserveAspectRatio="none"
								viewBox="0 0 680 360">
								<path d="M340 64v66M160 132h360M160 132v64M340 132v64M520 132v64" />
								<path d="M160 236h360M160 236v58M340 236v58M520 236v58" />
							</svg>
							{/* Empty placeholder cards illustrating the canvas. The section already carries
							    preview.label, so these are decorative like the rails and minimap around them. */}
							{treeNodes.map((node: TreeNode) => (
								<div
									key={node.id}
									aria-hidden="true"
									className={`${styles.nodeCard} ${node.className}`}>
									<div className={styles.nodeImage} />
									<span />
									<small />
								</div>
							))}
						</div>

						<div aria-hidden="true" className={styles.minimap}>
							<span />
							<span />
							<span />
							<span />
							<span />
						</div>
					</div>
				</section>
			</section>

			<section
				aria-label={t("landingPage:features.label", { brand })}
				className={styles.featureBand}
				id="features">
				{features.map((feature: FeatureItem) => (
					<article key={feature.icon} className={styles.feature}>
						<FeatureIcon name={feature.icon} />
						<div>
							<h2>{t(`landingPage:features.${feature.icon}.title`)}</h2>
							<p>{t(`landingPage:features.${feature.icon}.description`)}</p>
						</div>
					</article>
				))}
			</section>

			<section className={styles.trending} id="trending">
				<div className={styles.sectionHeader}>
					<h2>{t("landingPage:trending.heading")}</h2>
					<Link to={Paths.explore}>{t("landingPage:trending.viewAll")}</Link>
				</div>
				<TrendingTrees className={styles.trendingGrid} placeholderClassName={styles.trendingPlaceholder} />
			</section>

			<LandingFooter />
		</main>
	);
};

export default LandingPage;
